"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { COMPONENTS } from '../sitebuilder/form/formengine';
import { FormSectionHeader } from '../sitebuilder/form/formcomponents';
import { formatAsUSD } from '../foundation/utilities';
import { fetchUspsRatesServer } from './usps.server';
function USPSRateSelector(props) {
    return (_jsxs("div", { className: "usps-rate-selector", children: [_jsx("input", { type: "hidden", name: "shippingMethod", value: props.selectedRate?.serviceId ?? '' }), _jsx("input", { type: "hidden", name: "shippingCost", value: props.selectedRate?.rate != null ? props.selectedRate.rate.toFixed(2) : '' }), _jsx(FormSectionHeader, { title: "Shipping Info" }), _jsxs("div", { className: "pix-cart-usps-summary", children: [_jsxs("div", { children: [_jsx("strong", { children: "Origin Postal Code:" }), " ", props.shippingDefaults.originPostalCode ?? 'Not set'] }), _jsxs("div", { children: [_jsx("strong", { children: "Origin Country:" }), " ", props.shippingDefaults.originCountry ?? 'US'] }), _jsxs("div", { children: [_jsx("strong", { children: "Estimated Cart Weight:" }), " ", props.cartWeight ? `${props.cartWeight} lb` : '0 lb'] })] }), _jsx("h4", { children: "USPS Rates" }), !props.isLoading && props.rates.length === 0 ? (_jsx("div", { className: "pix-cart-usps-rate-placeholder", children: "Complete the Zip Code Field to see your USPS Rates." })) : null, props.error && _jsx("div", { className: "pix-cart-error", children: props.error }), props.rates.length > 0 ? (_jsx("div", { className: "pix-cart-usps-rates", children: props.rates.map((rate) => (_jsx("div", { className: "pix-cart-usps-rate-option", children: _jsxs("label", { children: [_jsx("input", { type: "radio", name: "uspsRate", value: rate.rateId, checked: props.selectedRate?.rateId === rate.rateId, onChange: () => props.onSelectRate(rate) }), rate.serviceName, " \u2014 ", formatAsUSD(rate.rate), " ", rate.deliveryTime ? `(${rate.deliveryTime})` : ''] }) }, rate.rateId))) })) : null, props.selectedRate ? (_jsxs("div", { className: "pix-cart-usps-selected-rate", children: [_jsx("strong", { children: "Selected USPS Service:" }), " ", props.selectedRate.serviceName, " \u2014 ", formatAsUSD(props.selectedRate.rate)] })) : null] }));
}
COMPONENTS.USPSRateSelector = USPSRateSelector;
/**
 * USPSShippingForm is a React component that integrates USPS shipping rate selection into a checkout flow.
 *
 * @param props - The props for the USPSShippingForm component, including shipping data, form configuration, shopping cart items, and event handlers.
 */
USPSShippingForm.propTypes = {
    shippingDefaults: PropTypes.shape({
        originPostalCode: PropTypes.string,
        originCountry: PropTypes.string,
    }).isRequired,
    formRef: PropTypes.any,
    formRevision: PropTypes.number,
    rates: PropTypes.array,
    shoppingCart: PropTypes.arrayOf(PropTypes.shape({
        itemID: PropTypes.string.isRequired,
        itemTitle: PropTypes.string.isRequired,
        itemQuantity: PropTypes.number.isRequired,
        itemIsShippable: PropTypes.bool,
        itemWeight: PropTypes.number,
        itemWeightUnit: PropTypes.string,
    })).isRequired,
    selectedRate: PropTypes.object,
    onRatesChange: PropTypes.func,
    onSelectedRateChange: PropTypes.func,
};
export function USPSShippingForm(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const shippingDefaults = props.shippingDefaults;
    const selectedRate = props.selectedRate;
    const formRef = props.formRef;
    const formRevision = props.formRevision ?? 0;
    const lastFetchSignatureRef = useRef('');
    const latestRequestIdRef = useRef(0);
    const clearRates = useCallback(() => {
        props.onRatesChange?.([]);
        props.onSelectedRateChange?.(null);
    }, [props.onRatesChange, props.onSelectedRateChange]);
    const cartWeight = useMemo(() => props.shoppingCart.reduce((total, item) => {
        if (!item)
            return total;
        const quantity = Number(item.itemQuantity ?? 1);
        const weight = Number(item.itemWeight ?? 0);
        const unit = item.itemWeightUnit || 'lb';
        if (quantity <= 0 || weight <= 0)
            return total;
        const normalized = unit.toString().trim().toLowerCase() === 'kg'
            ? weight * 2.20462
            : unit.toString().trim().toLowerCase().startsWith('oz')
                ? weight / 16
                : Number(weight);
        return total + normalized * quantity;
    }, 0), [props.shoppingCart]);
    const fetchRatesForFormValues = useCallback(async (formValues) => {
        const fromZip = String(props.shippingDefaults.originPostalCode ?? '').replace(/\D/g, '').slice(0, 9);
        const fromCountry = String(props.shippingDefaults.originCountry ?? 'US').trim() || 'US';
        const toZip = String(formValues.zip ?? '').replace(/\D/g, '').slice(0, 9);
        const toCountry = String(formValues.country ?? '').trim() || 'US';
        const requestSignature = [
            toZip,
            toCountry,
            fromZip,
            fromCountry,
            cartWeight.toFixed(2),
        ].join('|');
        if (!fromZip) {
            setError('Origin postal code is required for USPS rates.');
            clearRates();
            return;
        }
        if (!(toZip.length === 5 || toZip.length === 9)) {
            lastFetchSignatureRef.current = '';
            clearRates();
            setError(null);
            return;
        }
        if (lastFetchSignatureRef.current === requestSignature) {
            return;
        }
        lastFetchSignatureRef.current = requestSignature;
        clearRates();
        setError(null);
        const requestId = ++latestRequestIdRef.current;
        setIsLoading(true);
        try {
            const weightOunces = Math.max(1, Math.round(cartWeight * 16));
            const result = await fetchUspsRatesServer({
                fromZip,
                fromCountry,
                toZip,
                toCountry,
                weightOunces,
            });
            if (latestRequestIdRef.current !== requestId) {
                return;
            }
            if (!Array.isArray(result)) {
                throw new Error('Invalid USPS rate response received.');
            }
            if (!result.length) {
                setError('No USPS rates were returned for the selected destination.');
            }
            else {
                props.onRatesChange?.(result);
            }
        }
        catch (err) {
            if (latestRequestIdRef.current === requestId) {
                setError(err?.message ?? 'Failed to fetch USPS rates.');
            }
        }
        finally {
            if (latestRequestIdRef.current === requestId) {
                setIsLoading(false);
            }
        }
    }, [cartWeight, clearRates, props.onRatesChange, props.shippingDefaults.originCountry, props.shippingDefaults.originPostalCode]);
    useEffect(() => {
        const form = formRef?.current;
        if (!form)
            return;
        const syncRatesFromForm = () => {
            if (!form)
                return;
            const formValues = Object.fromEntries(new FormData(form).entries());
            const normalizedZip = String(formValues.zip ?? '').replace(/\D/g, '').slice(0, 9);
            const normalizedCountry = String(formValues.country ?? '').trim() || 'US';
            void fetchRatesForFormValues({ zip: normalizedZip, country: normalizedCountry });
        };
        const handleInputOrChange = (event) => {
            const target = event.target;
            if (target && target.name && target.name !== 'zip' && target.name !== 'country') {
                return;
            }
            syncRatesFromForm();
        };
        syncRatesFromForm();
        form.addEventListener('input', handleInputOrChange);
        form.addEventListener('change', handleInputOrChange);
        return () => {
            form.removeEventListener('input', handleInputOrChange);
            form.removeEventListener('change', handleInputOrChange);
        };
    }, [fetchRatesForFormValues, formRef, formRevision]);
    function handleSelectRate(rate) {
        props.onSelectedRateChange?.(rate);
    }
    return (_jsx(USPSRateSelector, { shippingDefaults: shippingDefaults, cartWeight: Number(cartWeight.toFixed(2)), rates: props.rates ?? [], selectedRate: selectedRate ?? null, isLoading: isLoading, error: error, onSelectRate: handleSelectRate }));
}
COMPONENTS.USPSShippingForm = USPSShippingForm;
